// ============================================================
// components/CatalogoPanel.jsx — Panel principal Catálogo Comercial
// WeConnect CRM
// Creado: 29/05/2026
// NO TOCA: App.js lógica, Realtime, Atención, Soporte, Outbound
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';

import { authFetch } from '../utils';
import ProductTable from './catalogo/ProductTable';
import ProductForm from './catalogo/ProductForm';
import ProductPreview from './catalogo/ProductPreview';
import FacturacionTab from './catalogo/FacturacionTab';
import FacturacionForm from './catalogo/FacturacionForm';

const ZONAS = ['Almirante Brown', 'Florencio Varela', 'Capitán Sarmiento'];

function CatalogoPanel() {
  // Estado principal
  const [tab, setTab] = useState('productos'); // 'productos' | 'facturacion'
  const [zona, setZona] = useState('Almirante Brown');

  // Datos
  const [productos, setProductos] = useState([]);
  const [reglas, setReglas] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [contextoIA, setContextoIA] = useState('');

  // UI
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [editingRegla, setEditingRegla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar datos
  const fetchProductos = useCallback(async () => {
    try {
      const res = await authFetch(`/api/catalogo/productos`);
      const data = await res.json();
      if (res.ok) {
        setProductos(data);
        // Si hay un producto seleccionado, actualizar su referencia
        if (selectedProduct) {
          const updated = data.find(p => p.id === selectedProduct.id);
          if (updated) setSelectedProduct(updated);
        }
      }
    } catch (err) {
      console.error('[CATALOGO] Error cargando productos:', err);
      setError('Error al cargar productos');
    }
  }, [selectedProduct]);

  const fetchReglas = useCallback(async () => {
    try {
      const res = await authFetch(`/api/catalogo/reglas-facturacion`);
      const data = await res.json();
      if (res.ok) setReglas(data);
    } catch (err) {
      console.error('[CATALOGO] Error cargando reglas:', err);
    }
  }, []);

  const fetchCiclos = useCallback(async () => {
    try {
      const res = await authFetch(`/api/catalogo/ciclos`);
      const data = await res.json();
      if (res.ok) setCiclos(data);
    } catch (err) {
      console.error('[CATALOGO] Error cargando ciclos:', err);
    }
  }, []);

  const fetchContextoIA = useCallback(async (z) => {
    try {
      const res = await authFetch(`/api/catalogo/contexto-ia?zona=${encodeURIComponent(z)}`);
      const data = await res.json();
      if (res.ok) setContextoIA(data.contexto || '');
    } catch (err) {
      console.error('[CATALOGO] Error cargando contexto IA:', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchProductos(), fetchReglas(), fetchCiclos()]);
      await fetchContextoIA(zona);
      setLoading(false);
    };
    loadAll();
  }, []);

  useEffect(() => {
    fetchContextoIA(zona);
  }, [zona, fetchContextoIA]);

  // Acciones CRUD productos
  const handleCreateProduct = async (producto) => {
    try {
      const res = await authFetch(`/api/catalogo/productos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(producto)
      });
      if (res.ok) {
        await fetchProductos();
        await fetchContextoIA(zona);
        setShowForm(false);
        return { ok: true };
      } else {
        const err = await res.json();
        return { ok: false, error: err.error || 'Error al crear' };
      }
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  const handleUpdateProduct = async (id, updates) => {
    try {
      const res = await authFetch(`/api/catalogo/productos/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        await fetchProductos();
        await fetchContextoIA(zona);
        setShowForm(false);
        setEditingProduct(null);
        return { ok: true };
      } else {
        const err = await res.json();
        return { ok: false, error: err.error || 'Error al actualizar' };
      }
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  const handleDeactivateProduct = async (id) => {
    if (!window.confirm('¿Desactivar este producto? No se eliminará, solo dejará de estar disponible.')) return;
    try {
      const res = await authFetch(`/api/catalogo/productos/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await fetchProductos();
        await fetchContextoIA(zona);
        if (selectedProduct?.id === id) setSelectedProduct(null);
      }
    } catch (err) {
      console.error('[CATALOGO] Error desactivando producto:', err);
    }
  };

  const handleToggleZone = async (productId, zone) => {
    const product = productos.find(p => p.id === productId);
    if (!product) return;

    let newZones;
    if (zone === 'Todas') {
      newZones = product.zones.includes('Todas') ? [] : ['Todas'];
    } else {
      const current = product.zones.filter(z => z !== 'Todas');
      const exists = current.includes(zone);
      const next = exists ? current.filter(z => z !== zone) : [...current, zone];
      newZones = next.length ? next : ['Todas'];
    }

    await handleUpdateProduct(productId, { zones: newZones });
  };

  const handleUpdateRegla = async (id, updates) => {
    try {
      const res = await authFetch(`/api/catalogo/reglas-facturacion/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        await fetchReglas();
        await fetchContextoIA(zona);
        setShowBillingForm(false);
        setEditingRegla(null);
        return { ok: true };
      } else {
        const err = await res.json();
        return { ok: false, error: err.error || 'Error al actualizar' };
      }
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  // Métricas — todos los productos, sin filtrar por zona
  const activos = productos.filter(p => p.active);
  const conTV = activos.filter(p => p.tv);
  const combos = activos.filter(p => p.type === 'Combo');
  const destacados = activos.filter(p => p.featured);

  if (loading) {
    return (
      <div className="cat-loading">
        <div className="cat-loading-spinner" />
        <p>Cargando catálogo comercial...</p>
      </div>
    );
  }

  return (
    <div className="cat-root">
      {/* Header */}
      <div className="cat-topbar">
        <div>
          <div className="cat-eyebrow">Módulo comercial</div>
          <h1 className="cat-title">Catálogo Comercial</h1>
          <p className="cat-subtitle">
            Planes, combos, TV, adicionales y reglas de facturación para alimentar la IA sin hardcodear precios.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="cat-tabs">
        <button
          className={`cat-tab ${tab === 'productos' ? 'active' : ''}`}
          onClick={() => setTab('productos')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
          Productos
        </button>
        <button
          className={`cat-tab ${tab === 'facturacion' ? 'active' : ''}`}
          onClick={() => setTab('facturacion')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="2"/><path d="M9 14l2 2 4-4"/></svg>
          Facturación
        </button>
      </div>

      {/* Metric cards */}
      {tab === 'productos' && (
        <div className="cat-metrics">
          <div className="cat-metric-card">
            <div className="cat-metric-icon violet">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
            </div>
            <div><span>{activos.length}</span><p>Productos activos</p></div>
          </div>
          <div className="cat-metric-card">
            <div className="cat-metric-icon blue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="15" rx="2" ry="2"/><polyline points="17 2 12 7 7 2"/></svg>
            </div>
            <div><span>{conTV.length}</span><p>Con TV</p></div>
          </div>
          <div className="cat-metric-card">
            <div className="cat-metric-icon green">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </div>
            <div><span>{combos.length}</span><p>Combos</p></div>
          </div>
          <div className="cat-metric-card">
            <div className="cat-metric-icon amber">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            </div>
            <div><span>{destacados.length}</span><p>Destacados IA</p></div>
          </div>
        </div>
      )}

      {/* Contenido según tab */}
      {tab === 'productos' && (
        <div className="cat-content-grid">
          <ProductTable
            productos={productos}
            zona={zona}
            selectedId={selectedProduct?.id}
            onSelect={(p) => setSelectedProduct(p)}
            onNew={() => { setEditingProduct(null); setShowForm(true); }}
            onEdit={(p) => { setEditingProduct(p); setShowForm(true); }}
            onDeactivate={handleDeactivateProduct}
          />
          <ProductPreview
            product={selectedProduct}
            zona={zona}
            onToggleZone={handleToggleZone}
            contextoIA={contextoIA}
          />
        </div>
      )}

      {tab === 'facturacion' && (
        <FacturacionTab
          reglas={reglas}
          ciclos={ciclos}
          zona={zona}
          contextoIA={contextoIA}
          onEditRegla={(r) => { setEditingRegla(r); setShowBillingForm(true); }}
        />
      )}

      {/* Modal Producto */}
      {showForm && (
        <ProductForm
          product={editingProduct}
          onSave={editingProduct
            ? (data) => handleUpdateProduct(editingProduct.id, data)
            : handleCreateProduct
          }
          onClose={() => { setShowForm(false); setEditingProduct(null); }}
        />
      )}

      {/* Modal Facturación */}
      {showBillingForm && editingRegla && (
        <FacturacionForm
          regla={editingRegla}
          onSave={(data) => handleUpdateRegla(editingRegla.id, data)}
          onClose={() => { setShowBillingForm(false); setEditingRegla(null); }}
        />
      )}

      {/* Error toast */}
      {error && (
        <div className="cat-error-toast" onClick={() => setError(null)}>
          {error} — Click para cerrar
        </div>
      )}
    </div>
  );
}

export default CatalogoPanel;
